/**
 * iconSubjects.js — SUBJECT / THEME categories for the Segment icon picker
 * (DESIGN-DEBT-REGISTER DL-045; deck
 * `design-handoff/concepts/adaptive-icons-elevated/04-segment-icon-picker.html`).
 *
 * THE GAP THIS CLOSES (measured, not assumed): the icon library's category tabs
 * are the FontAwesome STYLE families — `faCategories.js` exports exactly
 * `Regular`, `Brands`, `Solid`, and `iconSource.js` adds a `More` catch-all.
 * Those are a rendering detail of the icon pack. A person choosing an icon for a
 * "Travel" or "Fitness" Segment does not think "is that one Regular or Solid?",
 * so today the tabs do nothing for them and the only real way through 1,454
 * icons is to already know the icon's name. The deck asks for subject/theme
 * categories instead.
 *
 * HOW, WITHOUT HAND-LABELLING 1,454 ICONS: every icon's own name is the label.
 * `tokenizeIconName` (iconCatalog.js) already splits `FaPlaneDeparture` into
 * `["plane","departure"]`, so a subject is decided by matching those tokens
 * against a curated keyword set. That keeps this file a readable ~20-entry
 * taxonomy instead of a 1,454-row mapping that would rot the moment the icon
 * pack updates, and it classifies any icon added later for free.
 *
 * Order is significant: SUBJECTS is scanned top-down and the FIRST match wins,
 * so narrower subjects must precede broader ones (a "heart" is Health before it
 * is a Symbol; a "bus" is Transport before it is Travel).
 *
 * Pure: no React, no DOM, no I/O — every function here is unit-testable.
 */

import { buildIconIndex, searchIcons, tokenizeIconName } from "./iconCatalog";

/** The tab shown when no subject filter is applied. */
export const ALL_SUBJECT = "All";

/** Where anything unmatched lands, so no icon is ever unreachable. */
export const OTHER_SUBJECT = "Other";

/**
 * The curated taxonomy. `id` doubles as the tab label (US spelling), so there is
 * one string to change, not two. `keywords` are matched against an icon's NAME
 * TOKENS — whole-token equality or token-prefix, never a loose substring, so
 * "car" cannot drag in "carrot" or "scarf".
 */
export const SUBJECTS = [
  {
    // T22.90 (Kelly 2026-10-10): numbers first -- "the most important" icons.
    // Assigned by NUMBERED_ICON_RE in subjectForIcon (a digit glyph's name
    // tokenizes to "number5", which no keyword can match).
    id: "Numbers",
    keywords: ["number", "numbers", "digit"],
  },
  {
    id: "People",
    keywords: [
      "user", "users", "person", "people", "child", "baby", "male",
      "female", "friends", "group", "face", "smile", "grin", "frown",
      "meh", "portrait", "id", "address", "contact", "team",
      "handshake", "hand", "thumbs", "laugh", "kiss", "sad", "beam",
      "wink", "squint", "flushed", "grimace", "dizzy", "tired", "angry",
      "surprise", "rolling", "head", "hat", "cowboy", "jedi", "mars",
      "venus", "mask", "ghost", "skull", "blind", "deaf", "pray",
    ],
  },
  {
    id: "Health",
    keywords: [
      "heart", "heartbeat", "pulse", "medkit", "medical", "hospital",
      "doctor", "nurse", "pill", "pills", "prescription", "syringe",
      "stethoscope", "tooth", "brain", "lungs", "dna", "virus",
      "bacteria", "wheelchair", "crutch", "bandage", "thermometer",
      "notes", "clinic", "ambulance", "aid", "radiation", "biohazard",
    ],
  },
  {
    id: "Fitness & Sports",
    keywords: [
      "running", "run", "walking", "biking", "swimmer", "swimming",
      "dumbbell", "football", "futbol", "basketball", "baseball",
      "volleyball", "golf", "hockey", "bowling", "tennis", "skiing",
      "snowboarding", "skating", "surfing", "climbing", "yoga",
      "trophy", "medal", "award", "gym", "fitness", "dice", "chess",
      "gamepad", "puzzle", "playing", "quidditch", "ring",
    ],
  },
  {
    id: "Food & Drink",
    keywords: [
      "utensils", "pizza", "burger", "hamburger", "hotdog", "bread",
      "cheese", "egg", "bacon", "drumstick", "shrimp", "carrot",
      "pepper", "lemon", "apple", "candy", "cookie", "ice", "cream",
      "birthday", "cake", "coffee", "mug", "beer", "wine", "cocktail",
      "glass", "bottle", "blender", "concierge",
    ],
  },
  {
    id: "Nature & Animals",
    keywords: [
      "tree", "leaf", "seedling", "flower", "spa", "cannabis", "frog",
      "fish", "dog", "cat", "horse", "cow", "hippo", "kiwi", "dove",
      "crow", "dragon", "spider", "feather", "paw", "mountain", "water",
      "fire", "recycle",
    ],
  },
  {
    id: "Weather & Time",
    keywords: [
      "sun", "moon", "cloud", "rain", "snow", "snowflake", "bolt",
      "wind", "umbrella", "rainbow", "temperature", "clock",
      "hourglass", "calendar", "stopwatch", "history", "alarm", "date",
      "day", "night",
    ],
  },
  {
    id: "Transport",
    keywords: [
      "car", "cars", "truck", "bus", "train", "subway", "tram", "taxi",
      "motorcycle", "bicycle", "bike", "plane", "helicopter", "rocket",
      "ship", "boat", "anchor", "gas", "parking", "traffic", "road",
      "shuttle", "van", "tractor",
    ],
  },
  {
    id: "Travel & Places",
    keywords: [
      "map", "marker", "location", "compass", "route", "directions",
      "globe", "passport", "suitcase", "luggage", "hotel", "campground",
      "camp", "landmark", "monument", "city", "building", "church",
      "mosque", "synagogue", "torii", "beach", "island", "flag",
      "world", "earth",
    ],
  },
  {
    id: "Home & Living",
    keywords: [
      "home", "house", "door", "key", "couch", "chair", "bath",
      "shower", "toilet", "sink", "bed", "lightbulb", "lamp", "fan",
      "plug", "broom", "trash", "box", "boxes", "tools", "hammer",
      "wrench", "screwdriver", "paint", "roller", "brush", "toolbox",
      "soap", "window", "can", "dolly", "faucet",
    ],
  },
  {
    id: "Work & Office",
    keywords: [
      "briefcase", "folder", "file", "files", "clipboard", "paperclip",
      "stapler", "printer", "fax", "calculator", "chart", "graph",
      "table", "columns", "project", "diagram", "sitemap", "tasks",
      "list", "check", "square", "signature", "stamp", "archive",
      "office", "business",
    ],
  },
  {
    id: "Money",
    keywords: [
      "dollar", "euro", "pound", "yen", "money", "coins", "coin",
      "wallet", "cash", "register", "credit", "card", "receipt",
      "invoice", "percent", "percentage", "piggy", "bank", "balance",
      "scale", "holding", "donate", "stripe", "paypal", "amazon", "pay",
      "visa", "mastercard", "bitcoin", "ethereum", "order",
    ],
  },
  {
    id: "Shopping",
    keywords: [
      "shopping", "cart", "basket", "bag", "store", "shop", "tag",
      "tags", "barcode", "qrcode", "shipping", "gift", "ticket",
      "coupon", "sale", "price",
    ],
  },
  {
    id: "Technology",
    keywords: [
      "laptop", "desktop", "computer", "mobile", "tablet", "keyboard",
      "mouse", "microchip", "memory", "server", "database", "hdd", "sd",
      "usb", "wifi", "bluetooth", "network", "code", "terminal", "bug",
      "robot", "cog", "cogs", "gear", "gears", "power", "battery",
      "satellite", "sim", "git", "github", "gitlab", "codepen",
      "chrome", "firefox", "edge", "safari", "digital", "hard", "drive",
      "log", "sliders", "clone", "fork",
    ],
  },
  {
    id: "Communication",
    keywords: [
      "comment", "comments", "envelope", "mail", "inbox", "paper",
      "phone", "bell", "bullhorn", "microphone", "headset", "rss",
      "share", "link", "at", "reply", "message", "chat", "sms",
      "broadcast", "facebook", "twitter", "instagram", "linkedin",
      "youtube", "twitch", "slack", "trello", "pocket", "discord",
      "reddit", "whatsapp", "telegram", "snapchat", "pinterest",
      "tumblr", "vimeo", "skype",
    ],
  },
  {
    id: "Media & Music",
    keywords: [
      "music", "headphones", "guitar", "drum", "play", "pause", "stop",
      "forward", "backward", "step", "volume", "camera", "video",
      "film", "photo", "image", "images", "podcast", "record", "tv",
      "radio", "compact", "disc", "audio", "gallery", "speaker", "mic",
      "voicemail", "captioning", "newspaper", "closed",
    ],
  },
  {
    id: "Education",
    keywords: [
      "book", "books", "bookmark", "graduation", "cap", "school",
      "university", "chalkboard", "pencil", "pen", "highlighter",
      "ruler", "atom", "flask", "microscope", "vial", "vials",
      "language", "quote", "font", "spell", "learn", "study",
    ],
  },
  {
    id: "Text & Editing",
    keywords: [
      "align", "bold", "italic", "underline", "justify", "center",
      "indent", "outdent", "paragraph", "heading", "strikethrough",
      "subscript", "superscript", "text", "format", "crop", "edit",
      "copy", "cut", "paste", "save", "border", "object", "ungroup",
       "layer", "layers", "eraser", "fill", "drip", "palette",
      "swatch", "magic", "wand",
    ],
  },
  {
    id: "Arrows",
    keywords: [
      "arrow", "arrows", "angle", "angles", "caret", "chevron", "long",
      "exchange", "sort", "level", "undo", "redo", "sync", "retweet",
      "expand", "compress", "sign", "left", "right", "up", "down",
      "corner", "rotate", "refresh", "ccw", "cw", "maximize",
      "minimize", "zoom", "trending", "download", "upload", "out",
      "side", "repeat", "random", "shuffle",
    ],
  },
  {
    // Populated purely by the icon pack's own "Brands" family (see
    // subjectForIcon) — brand marks are not classifiable from their names, and
    // scattering them through People/Money/Media would be worse than useless.
    id: "Brands",
    keywords: [],
  },
  {
    id: "Symbols",
    keywords: [
      "star", "circle", "dot", "asterisk", "plus", "minus", "times",
      "ban", "info", "question", "exclamation", "lock", "unlock",
      "shield", "eye", "search", "filter", "ellipsis", "bars", "grip",
      "th", "toggle", "certificate", "crown", "gem", "equal", "less",
      "greater", "than", "divide", "hash", "octagon", "copyright",
      "registered", "creative", "commons", "cc", "nc", "nd", "sa",
      "poll", "more", "zap", "life", "slash", "stroke", "not", "void",
    ],
  },
];

const SUBJECT_IDS = SUBJECTS.map((s) => s.id);

// A digit icon from the picker's Numbers set (iconLibrary.js).
const NUMBERED_ICON_RE = /^Tb(Circle|Square)?Number[0-9]$/;

/** Every tab id in display order: All, the curated subjects, then Other. */
export const ALL_SUBJECT_IDS = [ALL_SUBJECT, ...SUBJECT_IDS, OTHER_SUBJECT];

// Keyword -> subject id, built once. A keyword claimed by an earlier subject is
// never re-claimed by a later one, which is how "first match wins" is enforced
// cheaply (one Map lookup per token instead of scanning 18 keyword lists).
const KEYWORD_TO_SUBJECT = (() => {
  const map = new Map();
  SUBJECTS.forEach((subject) => {
    subject.keywords.forEach((kw) => {
      if (!map.has(kw)) map.set(kw, subject.id);
    });
  });
  return map;
})();

/**
 * Decide an icon's subject from its name (and, for brands, its source category).
 *
 * Brands are recognized by the icon pack itself, so an entry that came from the
 * pack's "Brands" family is a brand regardless of what its name tokens say —
 * otherwise `FaApple` would be filed under Food & Drink.
 *
 * @param {string} name raw icon name, e.g. "FaPlaneDeparture"
 * @param {string} [sourceCategory] the icon's originating pack family
 * @returns {string} a subject id, always one of ALL_SUBJECT_IDS (never "All")
 */
export const subjectForIcon = (name, sourceCategory) => {
  if (typeof sourceCategory === "string" && sourceCategory.toLowerCase() === "brands") {
    return "Brands";
  }
  // T22.90: TbNumber5 / TbCircleNumber5 / TbSquareNumber5 -> Numbers, ahead of
  // the token scan (else "circle" -> Symbols, "square" -> Work & Office).
  if (typeof name === "string" && NUMBERED_ICON_RE.test(name)) return "Numbers";
  const tokens = tokenizeIconName(name);
  // Scan tokens in order so the icon's leading word decides; only fall back to a
  // later token when the first is not a taxonomy word ("Alt", "Outline", ...).
  for (const token of tokens) {
    const hit = KEYWORD_TO_SUBJECT.get(token);
    if (hit) return hit;
  }
  // Singular/plural tolerance, second pass only — cheaper than doubling the map
  // and it keeps the keyword lists readable.
  for (const token of tokens) {
    if (token.length > 3 && token.endsWith("s")) {
      const hit = KEYWORD_TO_SUBJECT.get(token.slice(0, -1));
      if (hit) return hit;
    }
  }
  return OTHER_SUBJECT;
};

/**
 * Group a built icon index by subject.
 *
 * @param {{entries: Array}} index the result of buildIconIndex()
 * @returns {{bySubject: Object<string, Array>, subjects: string[],
 *            counts: Object<string, number>}}
 *          `subjects` lists ONLY the tabs that actually have icons (an empty tab
 *          is a dead control), always led by "All".
 */
export const buildSubjectIndex = (index) => {
  const entries = (index && Array.isArray(index.entries) && index.entries) || [];
  const bySubject = {};
  const seenPerSubject = {};

  entries.forEach((entry) => {
    if (!entry || !entry.name) return;
    const subject = subjectForIcon(entry.name, entry.category);
    if (!bySubject[subject]) {
      bySubject[subject] = [];
      seenPerSubject[subject] = new Set();
    }
    // The same icon can appear in two pack families; show it once per subject.
    if (seenPerSubject[subject].has(entry.name)) return;
    seenPerSubject[subject].add(entry.name);
    bySubject[subject].push(entry);
  });

  const counts = {};
  Object.keys(bySubject).forEach((k) => {
    counts[k] = bySubject[k].length;
  });

  const subjects = [
    ALL_SUBJECT,
    ...SUBJECT_IDS.filter((id) => counts[id] > 0),
    ...(counts[OTHER_SUBJECT] > 0 ? [OTHER_SUBJECT] : []),
  ];

  return { bySubject, subjects, counts };
};

/**
 * The picker's one filter entry point.
 *
 * Search stays GLOBAL and deliberately IGNORES the selected subject (row 35's
 * rule, kept): if someone types a word they should never have to also guess the
 * right tab. With no query, the subject selects the tab.
 *
 * @param {ReturnType<typeof import("./iconCatalog").buildIconIndex>} index
 * @param {ReturnType<typeof buildSubjectIndex>} subjectIndex
 * @param {{query?: string, subject?: string}} [opts]
 * @returns {Array} matching entries, ordered
 */
export const filterIconsBySubject = (index, subjectIndex, opts = {}) => {
  const query = String(opts.query || "").trim();
  if (query) return searchIcons(index, { query });

  const subject = opts.subject;
  if (!subject || subject === ALL_SUBJECT) {
    return (index && index.entries) || [];
  }
  return (subjectIndex && subjectIndex.bySubject[subject]) || [];
};

/**
 * Remap a `{ packFamily: [iconComponent] }` map into `{ subject: [iconComponent] }`.
 *
 * This is the drop-in seam for the LEGACY picker
 * (`SegmentSettings/children/SegmentIcon.jsx`), which consumes a category map of
 * raw icon components rather than an index. Swapping the map is the whole
 * integration there — every downstream tab, grid and search computation is
 * untouched, which keeps the flag-OFF path byte-identical.
 *
 * The leading "All" group is the full, de-duplicated catalog, so the first tab
 * still shows everything exactly as a user expects.
 *
 * @param {Object<string, Array>} categoryMap
 * @returns {Object<string, Array>} subject-keyed map, "All" first
 */
export const groupCategoryMapBySubject = (categoryMap) => {
  const index = buildIconIndex(categoryMap);
  const subjectIndex = buildSubjectIndex(index);

  const all = [];
  const seen = new Set();
  index.entries.forEach((e) => {
    if (!e || seen.has(e.name)) return;
    seen.add(e.name);
    all.push(e.icon);
  });

  const out = { [ALL_SUBJECT]: all };
  subjectIndex.subjects
    .filter((s) => s !== ALL_SUBJECT)
    .forEach((s) => {
      out[s] = subjectIndex.bySubject[s].map((e) => e.icon);
    });
  return out;
};
